import { IconInfoCircle, IconPlus, IconSearch, IconTrash } from '@tabler/icons-react';
import { Checkbox } from '@/components/ui/checkbox';
import Hint from '@/components/common/Hint';
import { cn } from '@/lib/utils';
import { sanitizeText } from '@/utils/sanitize';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { localizeDigits } from '../rateChartConfig';
import { TextField } from '@/components/common/EditorFields';

const QUERY_MAX = 60;

/**
 * Which services are on the poster and what they cost. These edits are for the poster only — the shop's real
 * price list in Settings is never touched. A blank price prints as "negotiable".
 */
export default function ServicesPanel({ items, off, lang, query, negotiable, onQuery, onToggle, onAll, onName, onPrice, onDelete, onAdd }) {
    const { t, i18n } = useTranslation();
    const ui = i18n.language?.startsWith('en') ? 'en' : 'bn';
    // The box keeps what was typed (spaces included); the filter itself only ever sees the sanitized text.
    const q = sanitizeText(query, { maxLen: QUERY_MAX }).toLowerCase();
    const shown = items.filter((it) => !q || `${it.names.bn} ${it.names.en}`.toLowerCase().includes(q));
    const onCount = items.filter((it) => !off[it.id]).length;

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 h-10 px-3 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 focus-within:ring-2 focus-within:ring-primary/40">
                <IconSearch size={16} className="text-zinc-400 shrink-0" />
                <input
                    value={query}
                    onChange={(e) => onQuery(e.target.value)}
                    maxLength={QUERY_MAX}
                    placeholder={t('rateChart.services.search')}
                    className="flex-1 min-w-0 bg-transparent text-sm text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 outline-none"
                />
            </div>

            <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0 text-[13px] text-zinc-600 dark:text-zinc-300">
                    {t('rateChart.services.inChart', { on: localizeDigits(onCount, ui), total: localizeDigits(items.length, ui) })}
                </div>
                <button type="button" onClick={() => onAll(true)} className="px-2.5 py-1.5 rounded-lg border border-zinc-200 dark:border-zinc-700 text-xs font-semibold text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800">{t('rateChart.services.allOn')}</button>
                <button type="button" onClick={() => onAll(false)} className="px-2.5 py-1.5 rounded-lg border border-zinc-200 dark:border-zinc-700 text-xs font-semibold text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800">{t('rateChart.services.allOff')}</button>
            </div>

            <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 overflow-hidden divide-y divide-zinc-100 dark:divide-zinc-800">
                {shown.map((it) => {
                    const isOn = !off[it.id];
                    return (
                        <div key={it.id} data-rc-svc={it.id} className={cn('flex items-center gap-1.5 pl-1.5 pr-2 py-1.5', !isOn && 'bg-zinc-50 dark:bg-zinc-800/40')}>
                            <Hint label={t('rateChart.services.toggle')}>
                                <label className="w-8 h-8 shrink-0 flex items-center justify-center cursor-pointer">
                                    <Checkbox
                                        checked={isOn}
                                        onCheckedChange={() => onToggle(it.id)}
                                        aria-label={t('rateChart.services.toggle')}
                                        className="h-5 w-5 rounded-md border-[1.8px]"
                                    />
                                </label>
                            </Hint>
                            <TextField
                                value={it.names[lang]}
                                onChange={(v) => onName(it.id, v)}
                                maxLen={80}
                                aria-label={t('rateChart.services.name')}
                                className={cn('flex-1 min-w-0 !h-9 !border-transparent !bg-transparent focus:!bg-white dark:focus:!bg-zinc-800 focus:!border-primary', !isOn && '!text-zinc-400 dark:!text-zinc-500')}
                            />
                            <TextField
                                value={it.price}
                                onChange={(v) => onPrice(it.id, v)}
                                maxLen={24}
                                placeholder={t('rateChart.services.price')}
                                aria-label={t('rateChart.services.price')}
                                className="!w-[84px] shrink-0 !h-9 !px-2 text-right font-semibold"
                            />
                            {it.custom ? (
                                <Hint label={t('rateChart.services.delete')}>
                                    <button type="button" onClick={() => onDelete(it.id)} aria-label={t('rateChart.services.delete')} className="w-7 h-7 shrink-0 flex items-center justify-center rounded-lg text-zinc-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors">
                                        <IconTrash size={15} />
                                    </button>
                                </Hint>
                            ) : (
                                <span className="w-7 shrink-0" />
                            )}
                        </div>
                    );
                })}
                {shown.length === 0 && <div className="py-6 text-center text-sm text-zinc-500 dark:text-zinc-400">{t('rateChart.services.empty')}</div>}
            </div>

            <button type="button" onClick={onAdd} className="flex items-center justify-center gap-1.5 h-10 rounded-lg border-[1.5px] border-dashed border-primary/50 text-primary text-sm font-semibold hover:bg-primary/5 transition-colors">
                <IconPlus size={16} stroke={2.4} />{t('rateChart.services.add')}
            </button>

            <div className="flex gap-2.5 p-3 rounded-xl bg-primary/5 dark:bg-primary/10">
                <IconInfoCircle size={18} className="text-primary shrink-0 mt-0.5" />
                <div className="text-xs leading-relaxed text-zinc-700 dark:text-zinc-300">{t('rateChart.services.blankNote', { label: negotiable })}</div>
            </div>
        </div>
    );
}
